import { useMemo, type ReactNode } from 'react'

import { PivotTable, type PivotMeasureColumn } from '@/components/pivot/PivotTable'
import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { renderCell } from '@/features/report/columns'
import { isSelected } from '@/lib/cross-filter'
import { shortName, type ReportField, type TableWidget } from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'
import type { ReportRow } from '@/services/cube/report'

import { field, selectableValue } from './helpers'
import { SelectButton, WidgetCard } from './shared'
import type { TableResult, WidgetSelection, WidgetStatus } from './types'

function TotalsFooter({
  rows,
  labelSpan,
  measures,
}: {
  rows: ReportRow[] | undefined
  labelSpan: number
  measures: ReportField[]
}) {
  if (!rows?.length) return null
  return (
    <TableFooter>
      {rows.map((row, index) => (
        <TableRow key={index} className="font-semibold">
          <TableCell colSpan={labelSpan}>{messages.report.total}</TableCell>
          {measures.map((item) => (
            <TableCell key={item.name}>{renderCell(item, row[item.name], row)}</TableCell>
          ))}
        </TableRow>
      ))}
    </TableFooter>
  )
}

/** Saf çizim: satırlar ve toplamlar prop'tan gelir; `selection` verilmezse etkileşimsizdir. */
export function TableWidgetView({
  widget,
  data,
  status = {},
  selection,
  printMode,
}: {
  widget: TableWidget
  data: TableResult | undefined
  status?: WidgetStatus
  selection?: WidgetSelection
  /** Baskı: çerçevesiz, kaydırmasız; başlık satırı her sayfada tekrarlanır (`print.css`). */
  printMode?: boolean
}) {
  const selections = selection?.selections ?? []
  const dimensions = useMemo(
    () => widget.dimensions.map((member) => field(widget, member)),
    [widget],
  )
  const measures = useMemo(() => widget.measures.map((member) => field(widget, member)), [widget])
  const labelSpan = Math.max(dimensions.length, 1)
  const images = useMemo(() => dimensions.filter((item) => item.image), [dimensions])
  const pivotLevels = useMemo(() => widget.pivot.map((member) => field(widget, member)), [widget])
  const leafDims = useMemo(
    () => dimensions.filter((item) => !item.image && !widget.pivot.includes(item.name)),
    [dimensions, widget],
  )
  const pivotColumns: PivotMeasureColumn[] = useMemo(
    () => measures.map((item) => ({ key: item.name, label: item.label, field: item })),
    [measures],
  )
  const first = widget.order[0]
  const sortBy = useMemo(
    () => (first && widget.measures.includes(first.member) ? first : undefined),
    [first, widget],
  )
  const hasSelection = selections.some((item) => item.source === widget.key)
  const isRowSelected = (row: ReportRow) =>
    dimensions.some((item) => {
      const value = selectableValue(item, row[item.name])
      return value !== undefined && isSelected(selections, widget.key, shortName(item.name), value)
    })
  const renderDimension = (item: ReportField, row: ReportRow) => {
    const content = renderCell(item, row[item.name], row)
    const value = selectableValue(item, row[item.name])
    if (!widget.crossFilter.emit || !selection || value === undefined) return content
    return (
      <SelectButton
        onSelect={(additive) =>
          selection.onSelect(
            { source: widget.key, member: shortName(item.name), value, kind: 'value' },
            additive,
          )
        }
      >
        {content}
      </SelectButton>
    )
  }

  const rows = data?.rows
  const totals = widget.totals ? data?.totals : undefined
  let body: ReactNode
  if (status.isLoading) body = <Loading rows={4} />
  else if (status.isError) body = <ErrorState error={status.error} onRetry={status.onRetry} />
  else if (!rows?.length) body = <Empty />
  else if (widget.pivot.length > 0)
    body = (
      <PivotTable
        rows={rows}
        levels={pivotLevels}
        leafDims={leafDims}
        columns={pivotColumns}
        images={images}
        sortBy={sortBy}
        currencyMember={measures.find((item) => item.currencyMember)?.currencyMember}
        totals={totals}
        selection={
          widget.crossFilter.emit && selection
            ? { source: widget.key, selections, onSelect: selection.onSelect }
            : undefined
        }
        renderValue={renderCell}
      />
    )
  else
    body = (
      <Table>
        <TableHeader>
          <TableRow>
            {dimensions.map((item) => (
              <TableHead key={item.name}>{item.image ? '' : item.label}</TableHead>
            ))}
            {measures.map((item) => (
              <TableHead key={item.name} className="text-right">
                {item.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => {
            const selected = isRowSelected(row)
            return (
              <TableRow
                key={index}
                className={cn(selected && 'bg-accent', hasSelection && !selected && 'opacity-50')}
              >
                {dimensions.map((item) => (
                  // Boyut metni kaydırılabilir: dar kartta ölçü sütunları kırpılmasın.
                  <TableCell
                    key={item.name}
                    className={cn(item.image ? 'w-20 py-1' : 'min-w-32 whitespace-normal')}
                  >
                    {renderDimension(item, row)}
                  </TableCell>
                ))}
                {measures.map((item) => (
                  <TableCell key={item.name}>{renderCell(item, row[item.name], row)}</TableCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
        <TotalsFooter rows={totals} labelSpan={labelSpan} measures={measures} />
      </Table>
    )

  return (
    <WidgetCard title={widget.title} busy={status.busy} printMode={printMode}>
      <div className={printMode ? undefined : 'overflow-x-auto'}>{body}</div>
    </WidgetCard>
  )
}
